import React, { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import GUI, { IGUIProps } from "../GUI";
import Select, { TPrimitiveId, TSelectRow } from "../Select/Select";
import {UpdateEvent} from "../GUIEvents"
import { usePath } from "../../libs/path/PathProvider";
import { TButtonSizes } from "../Button/Button";
import { useOverlay } from "../Overlay/OverlayProvider";
import ChevronRight from "../Icons/ChevronRight";

export type TMenuItemClickHandler<ID extends TPrimitiveId> = (id:ID) => void
export type TUrl = string|{
    path?:string
    query?:Record<string,any>
    hash?:string
}
export type TMenuRowOption<ID extends TPrimitiveId> = TSelectRow<ID> & {url?:TUrl,onClick?:TMenuItemClickHandler<ID>,children?:TMenuRow<ID>[]}
export type TMenuRow<ID extends TPrimitiveId> = TMenuRowOption<ID> | ID
export type TMenuProps<ID extends TPrimitiveId> = IGUIProps & {
    value?:ID
    defaultValue?:ID
    options:TMenuRow<ID>[]
    filter?:string
    size?:TButtonSizes
    divProps?:Omit<React.ComponentPropsWithoutRef<"div">, "className"|"defaultValue">,
    itemProps?:(option:TMenuRow<ID>) => React.ComponentPropsWithoutRef<"div">
    itemRenderer?:(option:TMenuRow<ID>) => JSX.Element|string
    onUpdate?:(e:UpdateEvent<ID,HTMLDivElement>) => void

    // submenu stuffs
    parentMenu?:IParentMenu
    overlayId?:string
}
export interface IMenu<ID extends TPrimitiveId> {
    value:ID|undefined
    filter:(filter:string|undefined) => void
    // submenu stuffs
    cancelClose:Function
}
export interface IParentMenu {
    cancelClose:Function
}

function filter<ID extends TPrimitiveId>(filter:string|undefined, options:TMenuRow<ID>[]) {
    if (!filter) return options
    const regex = new RegExp(filter, "i")
    return options.filter(item => regex.test(Select.text(item)))
}

export function useMenu<ID extends TPrimitiveId>(){
    const {setPath,setQuery,setHash} = usePath()
    const selectMenu = (option:TMenuRow<ID>) => {
        if (!Select.isSelect(option)) return
        if (option.url) {
            if (typeof(option.url)=="string") return setPath(option.url)
            const {path,query,hash} = option.url
            path && setPath(path)
            query && setQuery(query)
            hash && setHash(hash)
        }
        else if (option.onClick) option.onClick(Select.id(option))
    }
    return {selectMenu}
}

var g_id = 0

function _Menu<ID extends TPrimitiveId>(props:TMenuProps<ID>, ref?:ForwardedRef<IMenu<ID>>){
    const isManaged = props.hasOwnProperty("value")

    const {addOverlay,popOverlay,hasOverlay,cancelClose} = useOverlay()
    const [filtered,setFiltered] = useState<TMenuRow<ID>[]>(filter(props.filter, props.options))
    const [value,setValue] = useState<ID|undefined>(props.defaultValue ?? props.value)

    const {selectMenu} = useMenu<ID>()
    const current = isManaged ? props.value : value

    const refSelf = useRef<HTMLDivElement|null>(null)
    const mounted = useRef<boolean>(false)
    const menuId = useMemo(() => g_id++, [])
    const size = props.size || "regular"

    const onSelect = (option:TMenuRow<ID>, onClick?:React.MouseEventHandler<HTMLDivElement>) => (e:React.MouseEvent<HTMLDivElement>) => {
        const id = Select.id(option)
        if (!isManaged) setValue(id)
        props.onUpdate?.(new UpdateEvent(e.nativeEvent, id, refSelf.current))
        onClick?.(e)
        selectMenu(option)
        if (props.overlayId && !props.parentMenu) popOverlay(props.overlayId)
    }
    const onDown = (onPointerDown?:React.PointerEventHandler<HTMLDivElement>) => (e:React.PointerEvent<HTMLDivElement>) => {
        e.preventDefault()
        onPointerDown?.(e)
    }

    useImperativeHandle(ref, () => ({
        get value(){ return current},
        filter(text:string|undefined) { 
            setFiltered(filter(text, props.options)) 
        },
        cancelClose() { clearTimeout(closeSelfTimeout.current) }
    }), [current])

    useEffect(() => {
        if (mounted.current) setFiltered(filter(props.filter, props.options))
        else mounted.current = true
    }, [props.options, props.filter])

    //////////////////////////////////////////////////////////////////////
    // submenu stuffs
    const refParentMenu = useRef<IParentMenu|null>(null)
    const refSubMenu = useRef<IMenu<ID>|null>(null)
    const closeSubmenuTiemout = useRef<any>()
    const closeSelfTimeout = useRef<any>()

    useImperativeHandle(refParentMenu, () => ({
        cancelClose() {clearTimeout(closeSubmenuTiemout.current)}
    }), [])

    const onOpenSubMenu = (option:TMenuRowOption<ID>, children:TMenuRow<ID>[]) => (e:React.MouseEvent<HTMLDivElement>) => {
        const id = Select.id(option)
        const submenuId = menuId+"_"+id
        if (hasOverlay(submenuId)) {
            refSubMenu.current?.cancelClose()
            return cancelClose(submenuId)
        }
        const valueProps = isManaged ? {value:props.value} : {}
        const onUpdate = (e:UpdateEvent<ID>) => {
            popOverlay(submenuId)
            props.overlayId && popOverlay(props.overlayId)
            props.onUpdate?.(e)
        }
        const menu = <Menu overlayId={submenuId} options={children} {...props.divProps} size={size} defaultValue={props.defaultValue} {...valueProps} onUpdate={onUpdate} ref={refSubMenu} parentMenu={refParentMenu.current!}/>
        addOverlay(submenuId, {children:menu, anchor:{follow:`div[menu-id=${id}]`,anchorPosition:"right"}})
    }
    const onCloseSubMenu = (id:ID) => (e:React.MouseEvent<HTMLDivElement>) => {
        closeSubmenuTiemout.current = setTimeout(() => {
            popOverlay(menuId+"_"+id)
        }, 100)
    }
    const onMouseEnter = (e:React.MouseEvent<HTMLDivElement>) => {
        props.parentMenu?.cancelClose()
    }
    const onMouseLeave = (e:React.MouseEvent<HTMLDivElement>) => {
        if (!props.overlayId || !props.parentMenu) return
        closeSelfTimeout.current = setTimeout(() => popOverlay(props.overlayId), 100)
    }
    const options = filtered.length ? filtered.map(option => {
        const {className,onClick,onPointerDown,...itemProps} = props.itemProps?.(option) || {}
        const id = Select.id(option)
        const icon = Select.isSelect(option) ? option.icon : <></>
        const content = props.itemRenderer?.(option) ?? <>{icon} {Select.text(option)}</>
        var mouseEnter:any = {}, mouseLeave:any = {}, arrow:any = undefined, _click:any = undefined, _down:any
        if (Select.isSelect(option) && option.children){
            mouseEnter = {onMouseEnter:onOpenSubMenu(option, option.children)}
            mouseLeave = {onMouseLeave:onCloseSubMenu(option.id)}
            arrow = <ChevronRight className="arrow"/>
            _down = (e:React.PointerEvent<HTMLDivElement>) => {
                e.preventDefault()
                e.stopPropagation()
            }
        }
        else {
            _click = onSelect(option, onClick)
            _down = onDown(onPointerDown)
        }
        const menuItem = <div key={id} menu-id={id} className={GUI.joinClass("menuItem",className,current==id ? "selected" : "", size)} onPointerDown={_down} onClick={_click} {...itemProps} {...mouseEnter} {...mouseLeave}>
            <div className="flex1 text">{content}</div>
            {arrow}
        </div>
        return menuItem
    }) : <div className="empty">List is empty</div>
    // end of submenu stuffs
    //////////////////////////////////////////////////////////////////////

    return <div menu-id={menuId} className={GUI.joinClass("menu", props.className, size)} ref={refSelf} {...props.divProps} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>{options}</div>
}

const Menu = forwardRef(_Menu) as <ID extends TPrimitiveId>(props:TMenuProps<ID> & {ref?:ForwardedRef<IMenu<ID>>}) => JSX.Element
export default Menu
